import { useState, useMemo } from "react";
import { X, ArrowLeftRight, Loader2 } from "lucide-react";
import { formatINR, formatDate } from "../../lib/api";

// Preview matched internal-transfer pairs and confirm only the real ones.
export function TransferReviewModal({ review, onClose, confirmTransfers }) {
  const pairs = review?.pairs || [];
  const [selected, setSelected] = useState(() => new Set(pairs.map((_, i) => i)));
  const [saving, setSaving] = useState(false);

  const toggle = (i) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(i)) n.delete(i); else n.add(i);
    return n;
  });
  const allOn = selected.size === pairs.length && pairs.length > 0;
  const toggleAll = () => setSelected(allOn ? new Set() : new Set(pairs.map((_, i) => i)));

  const total = useMemo(() => pairs.filter((_, i) => selected.has(i)).reduce((s, p) => s + (p.amount || 0), 0), [pairs, selected]);

  async function confirm() {
    setSaving(true);
    await confirmTransfers(pairs.filter((_, i) => selected.has(i)));
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="transfer-review-modal">
      <div className="absolute inset-0 bg-[#1A1A18]/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white shadow-2xl rounded-lg overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E3DB]">
          <div className="flex items-center gap-2">
            <ArrowLeftRight className="w-5 h-5 text-[#C84B31]" />
            <h3 className="font-headings font-bold text-lg text-[#1A1A18]">Review internal transfers</h3>
          </div>
          <button onClick={onClose} data-testid="transfer-review-close" className="w-8 h-8 rounded-full hover:bg-[#F5F3EC] flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-3 border-b border-[#E5E3DB] text-sm text-[#68665E]">
          Found {pairs.length} same-day, same-amount pair(s) between your accounts. Untick any that are just a coincidence.
        </div>
        <div className="flex items-center justify-between px-5 py-2 border-b border-[#E5E3DB]">
          <label className="flex items-center gap-2 text-sm text-[#1A1A18]">
            <input type="checkbox" checked={allOn} onChange={toggleAll} data-testid="transfer-select-all" /> Select all
          </label>
          <span className="text-xs text-[#68665E]">{selected.size} selected · {formatINR(total)}</span>
        </div>
        <div className="overflow-y-auto flex-1 divide-y divide-[#F0EEE8]">
          {pairs.map((p, i) => (
            <label key={i} className="flex items-start gap-3 px-5 py-3 hover:bg-[#FAF9F6] cursor-pointer" data-testid={`transfer-pair-${i}`}>
              <input type="checkbox" checked={selected.has(i)} onChange={() => toggle(i)} className="mt-1" data-testid={`transfer-pair-check-${i}`} />
              <div className="flex-1 min-w-0 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-[#1A1A18]">{formatINR(p.amount)}</span>
                  <span className="text-xs text-[#68665E]">{formatDate(p.date)}</span>
                </div>
                <div className="mt-1 text-xs text-[#4F4C44]">
                  <span className="text-[#8A2526] font-medium">{p.debit.account_label}</span>
                  <span className="mx-1">→</span>
                  <span className="text-[#2F6F4E] font-medium">{p.credit.account_label}</span>
                </div>
                <div className="mt-0.5 text-[11px] text-[#8A8676] truncate">{p.debit.description}</div>
              </div>
            </label>
          ))}
        </div>
        <div className="px-5 py-4 border-t border-[#E5E3DB] flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-4 py-2 border border-[#E5E3DB] hover:bg-[#F5F3EC] rounded">Cancel</button>
          <button onClick={confirm} disabled={saving || selected.size === 0} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="transfer-confirm-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowLeftRight className="w-4 h-4" />} Tally {selected.size} transfer(s)
          </button>
        </div>
      </div>
    </div>
  );
}
